import { Hand, Loader2, MessageSquare, Sparkles, Zap } from "lucide-react";
import type { FC } from "react";

import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import type { ConversationKind } from "../conversationGroups";

const KIND_ICON = {
  workflow: Sparkles,
  automation: Zap,
  chat: MessageSquare
} as const;

const Dot: FC = () => (
  <span aria-hidden className="shrink-0 text-fg-4">
    ·
  </span>
);

type Props = {
  kind: ConversationKind;
  /** The title as shown; truncated with an ellipsis. */
  title: string;
  /** The full title, shown in the tooltip. */
  fullTitle: string;
  /** A quiet label before the time ("Research" under a workflow thread). */
  subtitle: string | null;
  /** "9:53 AM", "Yesterday"; empty when the thread has no time. */
  time: string;
  active: boolean;
  running: boolean;
  waiting: boolean;
  /** What the user waits on, for the status mark. */
  waitingLabel: string;
  unread: number;
  onSelect: () => void;
};

/**
 * One conversation in the maximized Casey's sidebar: a kind icon, the title,
 * and a quiet second line with the status, subtitle and time. The selected
 * row takes the lavender accent look.
 */
export const ConversationRow: FC<Props> = ({
  kind,
  title,
  fullTitle,
  subtitle,
  time,
  active,
  running,
  waiting,
  waitingLabel,
  unread,
  onSelect
}) => {
  const Icon = KIND_ICON[kind];
  const status = waiting ? (
    <span
      className="flex shrink-0 items-center gap-1 text-warn"
      data-testid="casey-sidebar-status"
    >
      <Hand className="size-3" aria-label={waitingLabel} />
      <span aria-hidden>Needs You</span>
    </span>
  ) : running ? (
    <span
      className="flex shrink-0 items-center gap-1"
      data-testid="casey-sidebar-status"
    >
      <Loader2 className="size-3 animate-spin" aria-label="Casey working" />
      <span aria-hidden>Running</span>
    </span>
  ) : null;
  return (
    <Tooltip delayDuration={600}>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-testid="casey-sidebar-thread"
          data-kind={kind}
          data-active={active || undefined}
          aria-current={active ? "page" : undefined}
          onClick={onSelect}
          className={cn(
            "group flex w-full min-w-0 items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors duration-100 outline-none in-data-[density=compact]:py-1 hover:bg-fill-1 focus-visible:ring-2 focus-visible:ring-primary/40 data-active:bg-accent-soft data-active:inset-ring data-active:inset-ring-accent-line"
          )}
        >
          <span
            aria-hidden
            data-slot="conversation-kind"
            className={cn(
              "flex size-6 shrink-0 items-center justify-center rounded-md [&_svg]:size-3.5",
              kind === "workflow"
                ? "bg-accent-soft text-accent-seed"
                : active
                ? "bg-fill-2 text-fg-2"
                : "bg-fill-1 text-fg-3"
            )}
          >
            <Icon />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span
              className={cn(
                "truncate text-base",
                active ? "font-medium text-fg" : "text-fg-2 group-hover:text-fg"
              )}
            >
              {title}
            </span>
            <span className="flex min-w-0 items-center gap-1 text-sm text-fg-3">
              {subtitle ? (
                <span className="min-w-0 truncate">{subtitle}</span>
              ) : null}
              {subtitle && (status || time) ? <Dot /> : null}
              {status}
              {status && time && !subtitle ? <Dot /> : null}
              {!status || !subtitle ? (
                time ? (
                  <span className="shrink-0 text-fg-4 tabular-nums">
                    {time}
                  </span>
                ) : null
              ) : null}
            </span>
          </span>
          {unread > 0 ? (
            <Badge
              variant="default"
              className="h-4 min-w-4 shrink-0 px-1 text-xs tabular-nums"
              aria-label={`${unread} unread mentions`}
            >
              {unread > 99 ? "99+" : unread}
            </Badge>
          ) : null}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">{fullTitle}</TooltipContent>
    </Tooltip>
  );
};
